/**
 * Compatibility shim: Material-UI v4 `@material-ui/core/styles` + `@material-ui/styles`
 * surface, implemented on @mui/material v7 + tss-react.
 *
 * The ported tree uses the v4 `makeStyles`/`withStyles` API:
 *   const useStyles = makeStyles((theme) => ({ root: {...} }));
 *   const classes = useStyles(props);          // <- returns the classes object directly
 *
 * tss-react's native API returns `{ classes }`; these wrappers preserve the v4
 * call shape so the ~48 makeStyles call sites need no edits.
 */
import * as React from 'react';
import { makeStyles as tssMakeStyles } from 'tss-react/mui';

export {
  ThemeProvider,
  createTheme,
  useTheme,
  styled,
  alpha,
  darken,
  lighten,
  emphasize,
  useThemeProps,
} from '@mui/material/styles';

// v4's `fade` was renamed to `alpha` in v5.
export { alpha as fade } from '@mui/material/styles';

// v5 removed createStyles (it was only a TS identity helper).
export const createStyles = <T,>(styles: T): T => styles;

type StylesArg = ((theme: any) => Record<string, any>) | Record<string, any>;

/**
 * v4 makeStyles allowed function values keyed on props, both at rule level
 * (`root: (props) => ({...})`) and at property level (`color: (props) => '#fff'`).
 * tss-react has no equivalent, so we pass the call-site argument through as tss
 * "params" and resolve any function values against it before handing styles to tss.
 */
function resolveFunctionValues(obj: Record<string, any>, params: any): Record<string, any> {
  const out: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'function') {
      const resolved = value(params);
      out[key] = resolved && typeof resolved === 'object' ? resolveFunctionValues(resolved, params) : resolved;
    } else if (value && typeof value === 'object' && !Array.isArray(value)) {
      out[key] = resolveFunctionValues(value, params);
    } else {
      out[key] = value;
    }
  }
  return out;
}

export function makeStyles(styles: StylesArg, _options?: unknown) {
  const stylesFn = typeof styles === 'function' ? styles : () => styles;
  const useStyles = tssMakeStyles<any>()((theme, params) =>
    resolveFunctionValues(stylesFn(theme) as Record<string, any>, params)
  );
  return (params?: any): Record<string, string> => useStyles(params).classes;
}

export function withStyles(styles: StylesArg, _options?: unknown) {
  const useStyles = makeStyles(styles);
  return function <P extends { classes?: Record<string, string> }>(
    Component: React.ComponentType<P>
  ) {
    const Wrapped = (props: P) => {
      const classes = useStyles(props);
      const merged = { ...classes, ...(props.classes ?? {}) };
      return React.createElement(Component, { ...props, classes: merged });
    };
    Wrapped.displayName = `WithStyles(${Component.displayName || Component.name || 'Component'})`;
    return Wrapped;
  };
}
